import _ from 'lodash'
import { generateDeferredPromise } from '@utils/DeferredPomiseUtil'
import { getStoreId } from '@utils/EnvironmentUtil'
import {
    CountMetric,
    LatencyMetric,
    AudioRecordingException,
    Exception,
    InvalidInputException,
    RecordingAlreadyInProgressException,
    UnsupportedBrowserException,
} from '@amzn/ihm-associate-tools-js-lib'

// To access window property MediaRecorder
declare global {
    // tslint:disable-next-line
    interface Window {
        MediaRecorder: any
    }
}

let initPromise
let audioContext
let audioStream
let recorder
let currentDeferred

/**
 * Service that handles audio recording and decoding so that it is usable by Voice.
 */

/**
 * Warms up the service so that the first call to recordAudio doesn't have to go through all of the
 * initial setup. This call can fail if the user hasn't interacted with the tools. Callers are
 * encouraged to retry (as long as it isn't an UnsupportedBrowserException) since the failure
 * may be transient.
 *
 * @return {Promise} Resolves if the warmup was successful and rejects otherwise.
 */
export const warmup = () => {
    const warmupFaultMetric = new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'audioRecordingService.warmup')
    const warmupLatencyMetric = new LatencyMetric(getStoreId(), 'Time', 'IhmFlyingDutchman', 'audioRecordingService.warmup')
    warmupLatencyMetric.start()
    return init()
        .then(() => {
            warmupLatencyMetric.stop()
            warmupFaultMetric.count(0)
        })
        .catch((e) => {
            warmupFaultMetric.count(1)

            if (e instanceof Exception) {
                return Promise.reject(e)
            }

            return Promise.reject(new AudioRecordingException({
                message: 'Error initializing the audio recorder',
            }))
        })
}

/**
 * Records audio for the specified time. Returns a promise that will be resolved when the audio is recorded and
 * processed.
 *
 * @param {object} config - Object containing the configuration values to recordAudio. The only required key is
 *  'recordTimeInMillis', but has optional keys such as 'beforeStartCallback' and 'beforeStopCallback'.
 *  Example:
 *      {
 *          recordTimeInMillis: 2000 // Will return a promise to record audio for 2 seconds.
 *          beforeStartCallback: {Function}, // Called right before we start recording.
 *          beforeStopCallback: {Function} // Called before `stop` is called.
 *      }
 *
 * @return {Promise} A promise that resolves if the audio recording + processing is done successfully. It will reject if:
 *      - The required fields of the config are not passed in.
 *      - The browser doesn't support this recorder.
 *      - Another recording is already in progress.
 *      - Can't getUserMedia (typically because of lack of interaction from the user with the tools). Can also happen if the
 *          website is not loaded using https (https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia).
 */
export const recordAudio = (config) => {
    const recordingAlreadyInProgressCountMetric = new CountMetric(getStoreId(), 'RecordingAlreadyInProgress', 'IhmFlyingDutchman', 'audioRecordingService')
    if (currentDeferred) {
        recordingAlreadyInProgressCountMetric.count(1)
        return Promise.reject(new RecordingAlreadyInProgressException({
            message: 'A recording is already in progress',
        }))
    }
    recordingAlreadyInProgressCountMetric.count(0)

    // Resolved inside the setupRecorder function
    currentDeferred = generateDeferredPromise()

    // We copy it because ensureValidConfig modifies it
    const recordAudioConfig = config ? { ...config } : config

    ensureValidConfig(recordAudioConfig)
        .then(init)
        .then(setup)
        .then(() => {
            return recordAudioConfig.beforeStartCallback()
        })
        .then(start)
        .then(() => {
            return setTimeout(_.noop, recordAudioConfig.recordTimeInMillis)
        })
        .then(() => {
            return recordAudioConfig.beforeStopCallback()
        })
        .then(stop)
        .catch((e) => {
            console.warn('Error while trying to record audio', e)

            // We re-reject with the same exception if it's already an instance of Exception
            if (e instanceof Exception) {
                rejectCurrentDeferredIfExists(e)
                return
            }

            console.warn('Error while trying to record audio', e)

            rejectCurrentDeferredIfExists(new AudioRecordingException({
                message: 'Error while recording audio',
            }))
        })
        .finally(() => {
            closeStream(audioStream)
            audioStream = null
        })

    return currentDeferred.promise
}

// Private functions

/**
 * Validates the config object to make sure it has the required inputs and adds the
 * `beforeStartCallback` and `beforeStopCallback` (if they don't exist).
 *
 * @param {object} config - The config object containing the config values to record audio.
 *  Note that this parameter will be modified if `beforeStartCallback` or `beforeStopCallback`
 *  are not defined in it.
 *
 * @return {Promise} Resolves if the input values are valid. Rejcts otherwhise.
 */
const ensureValidConfig = (config) => {
    if (!config) {
        return Promise.reject(new InvalidInputException({
            message: 'The config argument is required',
        }))
    }

    if (!config.recordTimeInMillis || config.recordTimeInMillis <= 0) {
        return Promise.reject(new InvalidInputException({
            message: 'The recordTimeInMillis key is required in the config object and must be greater than 0',
        }))
    }

    config.beforeStartCallback = config.beforeStartCallback || _.noop
    config.beforeStopCallback = config.beforeStopCallback || _.noop

    return Promise.resolve()
}

/**
 * Initializes the service and caches the promise to make sure it is only initialized once. Note that
 * it removes the promise if the intiialization fails to make sure that it can be retried at a
 * later stage
 *
 * @return {Promise} A promise that is resolved when the initialization is done.
 */
const init = () => {
    if (initPromise) {
        return initPromise
    }

    initPromise = validateBrowserCompatibility()
        .then(initAudioContext)
        .catch((e) => {
            console.warn('Error initializing the audio recorder', e)
            initPromise = null
            return Promise.reject(e)
        })
    return initPromise
}

/**
 * Initializes the audioContext that we will use to decode the raw audio.
 *
 * @return {Promise} resolves when the audioContext is created. Rejects in case of failure.
 */
const initAudioContext = () => {
    try {
        audioContext = new window.AudioContext()
        return Promise.resolve()
    } catch (e) {
        console.error('Error initializing audioContext', e)
        return Promise.reject(e)
    }
}

/**
 * Sets up the media and the recorder along with the proper events for the current request.
 *
 * @return {Promise} A promise that resolves when the setup is done.
 */
const setup = () => {
    return setupMedia()
        .then(setupRecorder)
}

/**
 *  Sets up the media and returns an audio stream that can be used to record audio.
 *
 *  @return {Promise} Promise that resolves when the MediaStream {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaStream}
 *      is available. May reject if
 *          - The audio stream is not available (for example, the user decided not to consent to enable microphone).
 *          - We're requesting the audio stream too early and the user hasn't interacted with the tools yet.
 *          - The page was not rendered using HTTPS.
 */
const setupMedia = () => {
    const getUserMediaLatencyMetric = new LatencyMetric(getStoreId(),'Time', 'IhmFlyingDutchman', 'audioRecordingService.getUserMedia')
    getUserMediaLatencyMetric.start()

    // This could reject if the user hasn't interacted enough with the page.
    // Wrapped in a promise since getUserMedia returns a native promise
    return window.navigator.mediaDevices.getUserMedia({
        audio: true,
    })
    .then((stream) => {
        getUserMediaLatencyMetric.stop()
        audioStream = stream
        return stream
    })
    .catch((e) => {
        console.warn('Error with getUserMedia', e)
        return Promise.reject(new AudioRecordingException({
            message: 'Could not initialize user media',
        }))
    })
}

/**
 * Creates a global MediaRecorder instance and adds the proper events to it. This method
 * orchestrates the audio aggregation and the audio decoding/processing. This method also
 * contains the logic to resolve the currentDeferred promise once the decoding is done.
 * Note that this function doesn't return anything.
 */
const setupRecorder = (stream) => {
    let data = []
    recorder = new window.MediaRecorder(stream)

    /**
     * Resets the data array
     */
    recorder.addEventListener('start', (e) => {
        data = []
    })

    /**
     * Pushes to the current data array as data becomes available
     */
    recorder.addEventListener('dataavailable', (e) => {
        if (!e || !e.data) {
            return
        }
        data.push(e.data)
    })

    /**
     * Processes the aggregated data and resolves the currentDeferred promise
     * with the raw audio data
     */
    recorder.addEventListener('stop', (e) => {
        new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'audioRecordingService.recorder')
            .count(0)
        const processAudioLatencyMetric = new LatencyMetric(getStoreId(), 'Time', 'IhmFlyingDutchman', 'audioRecordingService.processAudio')
        processAudioLatencyMetric.start()

        closeStream(stream)

        // Promisifying because processAudio has a lot of synchronous code that could throw and wouldn't be
        // caught anywhere to reject currentDeferred. This could cause a deadlock since currentDeferred would
        // never get resolved.
        Promise.resolve()
            .then(() => {
                return processAudio(audioContext, data)
            })
            .then((buffer) => {
                processAudioLatencyMetric.stop()
                new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'audioRecordingService.recordAudio')
                    .count(0)
                currentDeferred.resolve(buffer)
                currentDeferred = null
            })
            .catch((error) => {
                rejectCurrentDeferredIfExists(error)
            })
    })

    /**
     * Rejects the currentDeferred since there was a problem
     */
    recorder.addEventListener('error', (e) => {
        new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'audioRecordingService.recorder')
            .count(1)
        console.error('Got error while recording', e)
        closeStream(stream)
        rejectCurrentDeferredIfExists(new AudioRecordingException({
            message: 'There was a problem while recording',
        }))
    })
}

/**
 * Stops all the tracks from the Stream so that the microphone can be used in other places.
 */
const closeStream = (stream) => {
    if (!stream) {
        console.debug('No streams found')
        return
    }
    try {
        console.debug('Closing stream', stream.id)
        stream.getAudioTracks()
            .forEach((track) => {
                track.stop()
            })
    } catch (e) {
        new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'audioRecordingService.closeStream')
            .count(1)
        console.error('Error closing the streams', e)
    }
}

/**
 * Returns a promise that will be resolved with the decoded audio. Note that this function could
 * throw as well since it has a lot of synchronous code.
 *
 * @param {AudioContext} context - The audioContext to use
 * @param {Array} data - Data captured from the recorder.
 *
 * @return {Promise} The decoded audio data.
 */
const processAudio = (context, data) => {
    const deferred = generateDeferredPromise()

    const blobData = new window.Blob(data)
    new CountMetric(getStoreId(), 'rawAudioSize', 'IhmFlyingDutchman', 'audioRecordingService.record')
        .count(blobData.size)
    const reader = new window.FileReader()
    const readerFaultMetric = new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'audioRecordingService.reader')

    /**
     * Decodes the audio contents and resolves/rejects the deferred defined in this function (not to be confused with the
     * currentDeferred).
     */
    reader.addEventListener('load', () => {
        readerFaultMetric.count(0)
        const decodeAudioDataFaultMetric = new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'audioRecordingService.decodeAudioData')
        const decodeAudioDataLatencyMetric = new LatencyMetric(getStoreId(), 'Time', 'IhmFlyingDutchman', 'audioRecordingService.decodeAudioData')

        decodeAudioDataLatencyMetric.start()
        decodeAudioData(context, reader.result)
            .then((buffer) => {
                decodeAudioDataLatencyMetric.stop()
                decodeAudioDataFaultMetric.count(0)
                deferred.resolve(buffer)
            })
            .catch((e) => {
                decodeAudioDataFaultMetric.count(1)
                console.error('There was a problem decoding the audio recording', e)
                deferred.reject(new AudioRecordingException({
                    message: 'There was a problem decoding the audio recording',
                }))
            })
    })

    /**
     * Rejects the current deferred in case of an error
     */
    reader.addEventListener('error', (e) => {
        readerFaultMetric.count(1)
        console.error('Reader error', e)
        deferred.reject(new AudioRecordingException({
            message: 'There was a problem while recording',
        }))
    })

    reader.readAsArrayBuffer(blobData)

    return deferred.promise
}

/**
 * Helper function that promisifies the decodeAudioData function
 *
 * @param {AudioContext} context - The context to use.
 * @param {ArrayBuffer} result - The result coming from a FileReader.
 *
 * @return {Promise} Resolves/Rejects when decodeAudioData either resolves or rejects.
 */
const decodeAudioData = (context, result) => {
    const deferred = generateDeferredPromise()
    if (audioContext && audioContext.decodeAudioData) {
        audioContext.decodeAudioData(result, deferred.resolve, deferred.reject)
    }
    return deferred.promise
}

/**
 * Rejects the currentDeferred if it exists and makes it null so that it can't be rejected/resolved
 * again and it unblocks other recordAudio requests.
 *
 * @param {Exception} The exception to reject with.
 */
const rejectCurrentDeferredIfExists = (exception) => {
    if (!currentDeferred) {
        console.warn('Could not reject currentDeferred with exception', exception)
        return
    }

    new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'audioRecordingService.recordAudio')
        .count(1)
    currentDeferred.reject(exception)
    currentDeferred = null
}

/**
 * Starts recording
 *
 * @return {Promise} Resolves if the recording started successfully and rejects if it didn't.
 */
const start = () => {
    const recorderStartFaultMetric = new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'audioRecordingService.start')

    try {
        recorder.start()
        recorderStartFaultMetric.count(0)
        return Promise.resolve()
    } catch (e) {
        console.warn('Failed to start the recorder', e)
        recorderStartFaultMetric.count(1)
        stop() // We don't wait for this
        return Promise.reject(e)
    }
}

/**
 * Stops recording
 *
 * @return {Promise} Resolved if the recording stopped successfully and Rejects if id didn't.
 */
const stop = () => {
    const recorderStopFaultMetric = new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'audioRecordingService.stop')

    try {
        recorder.stop()
        recorderStopFaultMetric.count(0)
        return Promise.resolve()
    } catch (e) {
        console.warn('Failed to stop the recorder', e)
        recorderStopFaultMetric.count(1)
        return Promise.reject(e)
    }
}

/**
 * Validates that the current browser is compatible to run this recording service.
 *
 * @return {Promise} Rejects if the browser is not compatible. Reolves if the browser is compatible.
 */
const validateBrowserCompatibility = () => {
    if (!window.navigator || !window.navigator.mediaDevices || !window.navigator.mediaDevices.getUserMedia) {
        new CountMetric(getStoreId(), 'Unsupported:getUserMedia', 'IhmFlyingDutchman', 'audioRecordingService')
            .count(1)
        return Promise.reject(new UnsupportedBrowserException({
            message: 'getUserMedia not supported on this browser',
        }))
    }

    if (!window.MediaRecorder) {
        new CountMetric(getStoreId(), 'Unsupported:MediaRecorder', 'IhmFlyingDutchman', 'audioRecordingService')
            .count(1)
        return Promise.reject(new UnsupportedBrowserException({
            message: 'MediaRecorder not supported on this browser',
        }))
    }

    if (!window.FileReader) {
        new CountMetric(getStoreId(), 'Unsupported:FileReader', 'IhmFlyingDutchman', 'audioRecordingService')
            .count(1)
        return Promise.reject(new UnsupportedBrowserException({
            message: 'FileReader not supported on this browser',
        }))
    }

    if (!window.Blob) {
        new CountMetric(getStoreId(), 'Unsupported:Blob', 'IhmFlyingDutchman', 'audioRecordingService')
            .count(1)
        return Promise.reject(new UnsupportedBrowserException({
            message: 'Blob not supported on this browser',
        }))
    }

    return Promise.resolve()
}
